import React, { useState, useEffect } from 'react';
import { Checkbox, Space } from 'antd';
import ConfirmationModal from '../../CustomComponents/ConfirmationModal';
import {
  ColumnSettingsContainer,
  ColumnCheckboxItem,
  ResetButton
} from './styledComponents';

const ColumnSettingsModal = ({
  visible,
  onCancel,
  onSave,
  visibleColumns,
  activeKey,
  activeTab1
}) => {
  const [tempColumns, setTempColumns] = useState(visibleColumns);

  // Update tempColumns when visibleColumns prop changes
  useEffect(() => {
    setTempColumns(visibleColumns);
  }, [visibleColumns]);

  const columnConfig = [
    // Default columns
    { key: 'jobId', label: 'Job ID', mandatory: true },
    { key: 'author', label: 'Author', mandatory: false },
    { key: 'patientName', label: 'Patient Name', mandatory: false },
    { key: 'jobNumber', label: 'Job Number', mandatory: false },
    { key: 'workType', label: 'Work Type', mandatory: false },
    { key: 'dictationTemplate', label: 'Dictation Template', mandatory: false },
    { key: 'biller', label: 'Biller', mandatory: false },
    { key: 'submissionTime', label: 'Submitted Time', mandatory: false },
    { key: 'effectiveDate', label: 'Encounter Date', mandatory: false },
    { key: 'facilityName', label: 'Facility', mandatory: false },
    // Conditional columns for In-Review, Ready, and In progress (jobs to review) tabs
    {
      key: 'currentLevel',
      label: 'Current level of review',
      mandatory: false,
      conditional: activeKey === '1' || activeKey === '4' || activeKey === '5' // Show for In-Review, Ready, and In progress tabs
    },
    {
      key: 'assignedTo',
      label: 'Current Assignee',
      mandatory: false,
      conditional: activeKey === '1' || activeKey === '4' || activeKey === '5' // Show for In-Review, Ready, and In progress tabs
    },
    // Conditional column for Completed tab
    {
      key: 'billStatus',
      label: 'Bill Status',
      mandatory: false,
      conditional: activeKey === '2' // Only show for Completed tab
    },
    {
      key: 'submissionToReviewerLevel1Time',
      label: 'Submission Time to Reviewer',
      mandatory: false
    },
    {
      key: 'emrSubmissionTime',
      label: 'EMR Submission Time',
      mandatory: false
    },
    // Conditional column for Incomplete tab
    {
      key: 'reasonForFailing',
      label: 'Reason For Failing',
      mandatory: false,
      conditional: activeKey === '3' // Only show for Incomplete tab
    },
    // Conditional column for My Jobs / In Progress tab
    {
      key: 'scribe',
      label: 'Scribe',
      mandatory: false,
      conditional: activeKey === '0' || activeKey === '1' || activeKey === '2' || activeKey === '3' || activeKey === '4' // Show for all My Jobs tabs and Jobs to Review / Ready
    },
    { key: 'admissionDate', label: 'Admission Date', mandatory: false },
    { key: 'mrn', label: 'MRN', mandatory: false },
    { key: 'dischargeDate', label: 'Discharge Date', mandatory: false },
    { key: 'primaryCarePhysician', label: 'Primary Care Physician', mandatory: false }
  ];

  const handleToggle = key => {
    const column = columnConfig.find(c => c.key === key);
    if (!column?.mandatory) {
      setTempColumns({ ...tempColumns, [key]: !tempColumns[key] });
    }
  };

  const handleCancel = () => {
    // Reset tempColumns to original visibleColumns when canceling
    setTempColumns(visibleColumns);
    onCancel();
  };

  const handleSave = () => {
    onSave(tempColumns);
    onCancel();
  };

  const handleReset = () => {
    const defaultColumns = {
      // Default visible columns
      jobId: true,
      author: true,
      patientName: true,
      jobNumber: true,
      workType: true,
      dictationTemplate: true,
      biller: false,
      submissionTime: false,
      effectiveDate: false,
      facilityName: false,
      currentLevel: false,
      assignedTo: false,
      billStatus: false,
      reasonForFailing: false,
      submissionToReviewerLevel1Time: false,
      emrSubmissionTime: false,
      scribe: false,
      admissionDate: false,
      mrn: false,
      dischargeDate: false,
      primaryCarePhysician: false,
      actions: true
    };
    setTempColumns(defaultColumns);
  };

  return (
    <ConfirmationModal
      visible={visible}
      onCancel={handleCancel}
      onOk={handleSave}
      header='Customize Columns'
      confirmation={true}
      antdModalProps={{ centered: true, width: 450, closable: false }}
    >
      {/* Kept above the scrollable list so it's reachable without scrolling */}
      <ResetButton onClick={handleReset}>Reset to Default</ResetButton>
      <ColumnSettingsContainer>
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
          {columnConfig
            .filter(col => col.conditional === undefined || col.conditional === true)
            .map(column => (
              <ColumnCheckboxItem key={column.key}>
                <Checkbox
                  checked={tempColumns[column.key]}
                  onChange={() => handleToggle(column.key)}
                  disabled={column.mandatory}
                >
                  {column.label}
                  {column.mandatory && (
                    <span style={{ color: '#999' }}> (Required)</span>
                  )}
                </Checkbox>
              </ColumnCheckboxItem>
            ))}
        </Space>
      </ColumnSettingsContainer>
    </ConfirmationModal>
  );
};

export default ColumnSettingsModal;
